import Vue from 'vue';
import Vuex from 'vuex';
import { ConsentDTO } from '@/models/ConsentDTO';
import { QuestionnaireDTO } from '@/models/QuestionnaireDTO';
import { QuestAddressDTO } from '@/models/QuestAddressDTO';
import { QuestInsuranceDTO } from '@/models/QuestInsuranceDTO';
import { QuestDoctorDTO } from '@/models/QuestDoctorDTO';
import { QuestQuestionDTO } from '@/models/QuestQuestionDTO';
import { QuestConsentDTO } from '@/models/QuestConsentDTO';
import moment from 'moment';

Vue.use(Vuex);

function initialState() {
  return {
    token: '',
    tokenTimestamp: 0,
    isAuthenticated: false,
    screeningUnit: 0,
    dataLoading: false,
    isLanguagesLoaded: false,
    isDossierOpened: false,
    consents: [] as ConsentDTO[],
    questionnaire: {} as QuestionnaireDTO,
    questionnaireValidationErrors: {},
    isAppLoaded: false,
    appVersion: {
      isLatest: false,
      lastCheckDate: new Date(),
    },
    hasGivenUp: false,
    giveUpClicked: false,
    isIdentified: false,
    localityNotInList: false,
    noDoctorInList: false,
    noDoctorOtherInList: false,
    showContentFromEquestAtHome: false,
  };
}

function dossierInitialState() {
  return {
    consents: [] as ConsentDTO[],
    questionnaire: {} as QuestionnaireDTO,
    questionnaireValidationErrors: {},
    hasGivenUp: false,
    giveUpClicked: false,
    localityNotInList: false,
    noDoctorInList: false,
    noDoctorOtherInList: false,
  };
}

export default new Vuex.Store({
  state: initialState(),

  getters: {
    fullname(state) {
      if (state.questionnaire.lastname != null && state.questionnaire.firstname != null) {
        if (state.questionnaire.maidenname != null && state.questionnaire.maidenname != undefined && state.questionnaire.maidenname.length > 0) {
          return `${state.questionnaire.lastname} ${state.questionnaire.firstname} (${state.questionnaire.maidenname})`;
        }
        return `${state.questionnaire.lastname} ${state.questionnaire.firstname}`;
      }
      return null;
    },

    hasSignatureExpired(state) {
      if (state.questionnaire && state.questionnaire.consentDate) {
        // If it's been more than 24 hours, the signature is considered expired
        const a = moment(state.questionnaire.consentDate);
        const b = moment();
        if (b.diff(a, 'hours', true) > 24.0) {
          return true;
        }
      }
      return false;
    },

    isPart1Valid(state, getters) {
      if (state.questionnaire.isIdentityValidated && state.questionnaire.consentDate && !getters.hasSignatureExpired) {
        return true;
      }
      return false;
    },

    isPart2Valid(state) {
      if (state.questionnaire.address && state.questionnaire.doctor && state.questionnaire.insurance) {
        if (state.questionnaire.address.isValidated && state.questionnaire.doctor.isValidated && state.questionnaire.insurance.isValidated) {
          return true;
        }
      }
      return false;
    },

    isPart3Valid(state) {
      if (state.questionnaire.questions && state.questionnaire.questions.isValidated) {
        return true;
      }
      return false;
    },

    allPartsValid(state, getters) {
      return getters.isPart1Valid && getters.isPart2Valid && getters.isPart3Valid;
    }
  },

  // Mutations have to be synchronous ! Use actions to do async operations.
  mutations: {
    resetState(state) {
      Object.assign(state, initialState());
    },

    resetDossierState(state) {
      Object.assign(state, dossierInitialState());
    },

    setToken(state, value: string) {
      state.token = value;
    },

    setTokenTimestamp(state, timestamp: number) {
      state.tokenTimestamp = timestamp;
    },
  
    setIsAuthenticated(state, status: boolean) {
      state.isAuthenticated = status;
    },
  
    setScreeningUnit(state, suId: number) {
      state.screeningUnit = suId;
    },
  
    setLoadingState(state, status: boolean) {
      state.dataLoading = status;
    },
  
    setIsLanguagesLoaded(state, status: boolean) {
      state.isLanguagesLoaded = status;
    },
  
    setIsDossierOpened(state, status: boolean) {
      state.isDossierOpened = status;
    },

    setConsentDate(state, value: Date) {
      state.questionnaire.consentDate = value;
    },

    setConsents(state, consents: ConsentDTO[]) {
      state.consents = consents;
    },

    setQuestionnaire(state, questionnaire: QuestionnaireDTO) {
      state.questionnaire = questionnaire;
    },

    setQuestionnaireValidationErrors(state, errors: object) {
      state.questionnaireValidationErrors = errors;
    },

    setQuestionnaireConsent(state, questConsent: QuestConsentDTO) {
      state.questionnaire.consent = questConsent;
    },

    setQuestionnaireAddress(state, questAddress: QuestAddressDTO) {
      state.questionnaire.address = questAddress;
    },

    setQuestionnaireInsurance(state, questInsurance: QuestInsuranceDTO) {
      state.questionnaire.insurance = questInsurance;
    },

    setQuestionnaireDoctor(state, questDoctor: QuestDoctorDTO) {
      state.questionnaire.doctor = questDoctor;
    },

    setQuestionnaireQuestions(state, questions: QuestQuestionDTO) {
      state.questionnaire.questions = questions;
    },

    setIsAppLoaded(state, value: boolean) {
      state.isAppLoaded = value;
    },

    setAppVersion(state, value: any) {
      state.appVersion = value;
    },

    setHasGivenUp(state, value: boolean) {
      state.hasGivenUp = value;
    },

    setGiveUpClicked(state, value: boolean) {
      state.giveUpClicked = value;
    },

    setEquestLng(state, value: string) {
      state.questionnaire.lngName = value;
    },

    setIsIdentified(state, status: boolean) {
      state.isIdentified = status;
    },

    setNoDoctorInList(state, status: boolean) {
      state.noDoctorInList = status;
    },

    setNoDoctorOtherInList(state, status: boolean) {
      state.noDoctorOtherInList = status;
    },

    setLocalityNotInList(state, status: boolean) {
      state.localityNotInList = status;
    },

    setShowContentFromEquestAtHome(state, value: boolean) {
      state.showContentFromEquestAtHome = value;
    }    
  },

  // Actions commit mutations and can contain arbitrary asynchronous operations
  actions: {
    setToken(context, value) {
      context.commit('setToken', value);
    },

    setTokenTimestamp(context, value) {
      context.commit('setTokenTimestamp', value);
    },
  
    setIsAuthenticated(context, value) {
      context.commit('setIsAuthenticated', value);
    },
  
    setScreeningUnit(context, value) {
      context.commit('setScreeningUnit', value);
    },
  
    setLoadingState(context, value) {
      context.commit('setLoadingState', value);
    },
  
    setIsLanguagesLoaded(context, value) {
      context.commit('setIsLanguagesLoaded', value);
    },
  
    setIsDossierOpened(context, value) {
      context.commit('setIsDossierOpened', value);
    },

    setConsents(context, value) {
      context.commit('setConsents', value);
    },

    setQuestionnaire(context, value) {
      context.commit('setQuestionnaire', value);
    },

    setHasGivenUp(context, value: boolean) {
      context.commit('setHasGivenUp', value);
    },

    setGiveUpClicked(context, value: boolean) {
      context.commit('setGiveUpClicked', value);
    },

    setIsIdentified(context, value) {
      context.commit('setIsIdentified', value);
    },

    setNoDoctorInList(context, value) {
      context.commit('setNoDoctorInList', value);
    },

    setNoDoctorOtherInList(context, value) {
      context.commit('setNoDoctorOtherInList', value);
    },

    setLocalityNotInList(context, value) {
      context.commit('setLocalityNotInList', value);
    },
    setShowContentFromEquestAtHome(context, value) {
      context.commit('setShowContentFromEquestAtHome', value);
    }
  },
  modules: {}
});
